import * as React from 'react';
import classNames from 'classnames';
import omit from 'rc-util/lib/omit';
import ClearableLabeledInput from './ClearableLabeledInput';
import { ConfigConsumer } from '../config-provider';
import SizeContext from '../config-provider/SizeContext';
import devWarning from '../_util/devWarning';
import { getInputClassName, hasPrefixSuffix } from './utils';
export function fixControlledValue(value) {
    if (typeof value === 'undefined' || value === null) {
        return '';
    }
    return value;
}
export function resolveOnChange(target, e, onChange, targetValue) {
    if (!onChange) {
        return;
    }
    let event = e;
    const originalInputValue = target.value;
    if (e.type === 'click') {
        // click clear icon
        event = Object.create(e);
        event.target = target;
        event.currentTarget = target;
        // change target ref value cause e.target.value should be '' when clear input
        target.value = '';
        onChange(event);
        // reset target ref value
        target.value = originalInputValue;
        return;
    }
    // Trigger by composition event, this means we need force change the input value
    if (targetValue !== undefined) {
        event = Object.create(e);
        event.target = target;
        event.currentTarget = target;
        target.value = targetValue;
        onChange(event);
        return;
    }
    onChange(event);
}
export function triggerFocus(element, option) {
    if (!element)
        return;
    element.focus(option);
    // Selection content
    const { cursor } = option || {};
    if (cursor) {
        const len = element.value.length;
        switch (cursor) {
            case 'start':
                element.setSelectionRange(0, 0);
                break;
            case 'end':
                element.setSelectionRange(len, len);
                break;
            default:
                element.setSelectionRange(0, len);
        }
    }
}
class Input extends React.Component {
    constructor(props) {
        super(props);
        this.direction = 'ltr';
        this.focus = (option) => {
            triggerFocus(this.input, option);
        };
        this.saveClearableInput = (input) => {
            this.clearableInput = input;
        };
        this.saveInput = (input) => {
            this.input = input;
        };
        this.onFocus = e => {
            const { onFocus } = this.props;
            this.setState({ focused: true }, this.clearPasswordValueAttribute);
            onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
        };
        this.onBlur = e => {
            const { onBlur } = this.props;
            this.setState({ focused: false }, this.clearPasswordValueAttribute);
            onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        };
        this.handleReset = (e) => {
            this.setValue('', () => {
                this.focus();
            });
            resolveOnChange(this.input, e, this.props.onChange);
        };
        this.renderInput = (prefixCls, size, bordered, input = {}) => {
            const { className, addonBefore, addonAfter, size: customizeSize, disabled } = this.props;
            // Fix https://fb.me/react-unknown-prop
            const otherProps = omit(this.props, [
                'prefixCls',
                'onPressEnter',
                'addonBefore',
                'addonAfter',
                'prefix',
                'suffix',
                'allowClear',
                // Input elements must be either controlled or uncontrolled,
                // specify either the value prop, or the defaultValue prop, but not both.
                'defaultValue',
                'size',
                'inputType',
                'bordered',
            ]);
            return (<input autoComplete={input.autoComplete} {...otherProps} onChange={this.handleChange} onFocus={this.onFocus} onBlur={this.onBlur} onKeyDown={this.handleKeyDown} className={classNames(getInputClassName(prefixCls, bordered, customizeSize || size, disabled, this.direction), {
                    [className]: className && !addonBefore && !addonAfter,
                })} ref={this.saveInput}/>);
        };
        this.clearPasswordValueAttribute = () => {
            // https://github.com/ant-design/ant-design/issues/20541
            this.removePasswordTimeout = setTimeout(() => {
                if (this.input &&
                    this.input.getAttribute('type') === 'password' &&
                    this.input.hasAttribute('value')) {
                    this.input.removeAttribute('value');
                }
            });
        };
        this.handleChange = (e) => {
            this.setValue(e.target.value, this.clearPasswordValueAttribute);
            resolveOnChange(this.input, e, this.props.onChange);
        };
        this.handleKeyDown = (e) => {
            const { onPressEnter, onKeyDown } = this.props;
            if (onPressEnter && e.keyCode === 13) {
                onPressEnter(e);
            }
            onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
        };
        this.renderComponent = ({ getPrefixCls, direction, input }) => {
            const { value, focused } = this.state;
            const { prefixCls: customizePrefixCls, bordered = true } = this.props;
            const prefixCls = getPrefixCls('input', customizePrefixCls);
            this.direction = direction;
            return (<SizeContext.Consumer>
        {size => (<ClearableLabeledInput size={size} {...this.props} prefixCls={prefixCls} inputType="input" value={fixControlledValue(value)} element={this.renderInput(prefixCls, size, bordered, input)} handleReset={this.handleReset} ref={this.saveClearableInput} direction={direction} focused={focused} triggerFocus={this.focus} bordered={bordered}/>)}
      </SizeContext.Consumer>);
        };
        const value = typeof props.value === 'undefined' ? props.defaultValue : props.value;
        this.state = {
            value,
            focused: false,
            // eslint-disable-next-line react/no-unused-state
            prevValue: props.value,
        };
    }
    static getDerivedStateFromProps(nextProps, { prevValue }) {
        const newState = { prevValue: nextProps.value };
        if (nextProps.value !== undefined || prevValue !== nextProps.value) {
            newState.value = nextProps.value;
        }
        return newState;
    }
    componentDidMount() {
        this.clearPasswordValueAttribute();
    }
    // Since polyfill `getSnapshotBeforeUpdate` need work with `componentDidUpdate`.
    // We keep an empty function here.
    componentDidUpdate() { }
    getSnapshotBeforeUpdate(prevProps) {
        if (hasPrefixSuffix(prevProps) !== hasPrefixSuffix(this.props)) {
            if (process.env.NODE_ENV !== 'production')
                devWarning(this.input !== document.activeElement, 'Input', `When Input is focused, dynamic add or remove prefix / suffix will make it lose focus caused by dom structure change. Read more: https://ant.design/components/input/#FAQ`);
        }
        return null;
    }
    componentWillUnmount() {
        if (this.removePasswordTimeout) {
            clearTimeout(this.removePasswordTimeout);
        }
    }
    blur() {
        this.input.blur();
    }
    setSelectionRange(start, end, direction) {
        this.input.setSelectionRange(start, end, direction);
    }
    select() {
        this.input.select();
    }
    setValue(value, callback) {
        if (this.props.value === undefined) {
            this.setState({ value }, callback);
        }
        else {
            callback === null || callback === void 0 ? void 0 : callback();
        }
    }
    render() {
        return <ConfigConsumer>{this.renderComponent}</ConfigConsumer>;
    }
}
Input.defaultProps = {
    type: 'text',
};
export default Input;
